import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col, Select } from 'antd';
import { ReactComponent as ArrowDownIcon } from '../../icons/arrow-down.svg';
import NewSearchInput from '../../UI/inputs/NewSearchInput';
import SelectInput from '../../UI/inputs/Select';
import FormItem from '../form/FormItem';
import {
    ManagementForm,
    ActionButton,
    StyledDownloadIcon
} from './SharedStyledComponents';
import { FilterOptions } from '../../hooks/useJobAssignments';
import { exportJobAssignmentsToCSV } from '../../utils/jobAssignmentsExport';
import { JobAssignment } from '../../hooks/useJobAssignments';

const { Option } = Select;

interface JobAssignmentsFiltersProps {
    filterOptions: FilterOptions;
    filterOptionsLoading: boolean;
    onFilter: (_: any, allValues: any) => void;
    assignments: JobAssignment[];
    filters: any;
}

const JobAssignmentsFilters: React.FC<JobAssignmentsFiltersProps> = ({
    filterOptions,
    filterOptionsLoading,
    onFilter,
    assignments,
    filters,
}) => {
    const [t] = useTranslation();

    const handleExport = () => {
        exportJobAssignmentsToCSV(assignments, t);
    };

    return (
        <ManagementForm>
            <Form
                scrollToFirstError={true}
                layout="horizontal"
                onFieldsChange={onFilter}
                initialValues={filters}
            >
                <Row justify="space-between" gutter={10} wrap={false}>
                    <Row justify="start" gutter={10}>
                        <Col>
                            <FormItem name="search">
                                <NewSearchInput
                                    placeholder={t('SEARCH_ASSIGNMENTS', 'Search agencies, jobs...')}
                                    style={{ width: '320px' }}
                                />
                            </FormItem>
                        </Col>
                        <Col>
                            <FormItem>
                                <ActionButton
                                    size="middle"
                                    onClick={handleExport}
                                >
                                    <StyledDownloadIcon />
                                    {t('EXPORT', 'Export')}
                                </ActionButton>
                            </FormItem>
                        </Col>
                    </Row>
                </Row>
                <Row justify="start" gutter={10}>
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 6 }}>
                        <FormItem name="status">
                            <SelectInput
                                showSearch
                                placeholder={t('STATUS', 'Status')}
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear
                            >
                                <Option value="active">{t('ACTIVE', 'Active')}</Option>
                                <Option value="inactive">{t('INACTIVE', 'Inactive')}</Option>
                            </SelectInput>
                        </FormItem>
                    </Col>
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 6 }}>
                        <FormItem name="client_company">
                            <SelectInput
                                showSearch
                                placeholder={t('CLIENT_COMPANY', 'Client Company')}
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear
                                loading={filterOptionsLoading}
                            >
                                {filterOptions.client_companies.map((company) => (
                                    <Option key={company} value={company}>
                                        {company}
                                    </Option>
                                ))}
                            </SelectInput>
                        </FormItem>
                    </Col>
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 6 }}>
                        <FormItem name="recruiter_company">
                            <SelectInput
                                showSearch
                                placeholder={t('RECRUITER_COMPANY', 'Agency')}
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear
                                loading={filterOptionsLoading}
                            >
                                {filterOptions.recruiter_companies.map((company) => (
                                    <Option key={company} value={company}>
                                        {company}
                                    </Option>
                                ))}
                            </SelectInput>
                        </FormItem>
                    </Col>
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 6 }}>
                        <FormItem name="job_title">
                            <SelectInput
                                showSearch
                                placeholder={t('JOB_TITLE', 'Job Title')}
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear
                                loading={filterOptionsLoading}
                            >
                                {filterOptions.job_titles.map((title) => (
                                    <Option key={title} value={title}>
                                        {title.length > 40 ? `${title.substring(0, 40)}...` : title}
                                    </Option>
                                ))}
                            </SelectInput>
                        </FormItem>
                    </Col>
                </Row>
            </Form>
        </ManagementForm>
    );
};

export default JobAssignmentsFilters; 